// src/pages/duplicateSentData/DuplicateSentDataLG.jsx - API Data Sent to Customers Logic

import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react';
import jwtAxios, {sseRequest} from '@crema/services/auth/jwt-auth';
import DuplicateSentDataUI from './DuplicateSentDataUI';
import AppInfoView from '@crema/core/AppInfoView';
import {useDispatch} from 'react-redux';
import {fetchError, showMessage} from 'redux/actions';
import {useAuthUser} from '@crema/utility/AuthHooks';

const DuplicateSentDataLG = () => {
  const dispatch = useDispatch();
  const {user} = useAuthUser();

  // Pagination and Data State
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const [dataToShow, setDataToShow] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Filter State (aligned with DataLG.jsx)
  const [filters, setFilters] = useState({
    country: '',
    dataSource: '',
    domain: '',
    customer: '',
    search: '',
    createdDateRange: [null, null],
    sentDateRange: [null, null],
    integrationSource: '',
  });

  // Debounced search (300ms)
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(filters.search || '');
    }, 300);
    return () => clearTimeout(id);
  }, [filters.search]);

  // Effective filters used for fetching (debounced search)
  const effectiveFilters = useMemo(
    () => ({
      ...filters,
      search: debouncedSearch,
    }),
    [
      filters.country,
      filters.dataSource,
      filters.domain,
      filters.customer,
      debouncedSearch,
      filters.createdDateRange,
      filters.sentDateRange,
      filters.integrationSource,
    ],
  );

  // Loading and Error States
  const [gettingData, setGettingData] = useState(false);
  const [selected, setSelected] = useState([]);

  // Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [editingDuplicate, setEditingDuplicate] = useState(null);
  const [viewingCustomers, setViewingCustomers] = useState([]);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [viewingLead, setViewingLead] = useState(null);

  // Filter Options
  const [filterOptions, setFilterOptions] = useState({
    statuses: [],
    emails: [],
    phones: [],
  });

  // Enhanced Send Functionality States
  const [domains, setDomains] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [emailSentHistory, setEmailSentHistory] = useState([]);
  const [sendingLoading, setSendingLoading] = useState(false);
  const [sendingResults, setSendingResults] = useState(null);

  // SSE Progress Tracking States
  const [allStatusCount, setAllStatusCount] = useState(0);
  const [doneStatusCount, setDoneStatusCount] = useState(0);
  const [errorStatusCount, setErrorStatusCount] = useState(0);
  const [archiveData, setArchiveData] = useState({});
  const [errorData, setErrorData] = useState([]);
  const [uploadFinished, setUploadFinished] = useState(false);
  const [currentSendData, setCurrentSendData] = useState(null);
  const archiveCalledRef = useRef(false);

  // Tab Management
  const [activeTab, setActiveTab] = useState(0);

  // Multi-Customer Duplicates State
  const [multiCustomerData, setMultiCustomerData] = useState([]);
  const [multiCustomerLoading, setMultiCustomerLoading] = useState(false);
  const [multiCustomerPagination, setMultiCustomerPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  // API vs System Overlap State
  const [apiSystemOverlapData, setApiSystemOverlapData] = useState([]);
  const [apiSystemOverlapLoading, setApiSystemOverlapLoading] = useState(false);
  const [apiSystemOverlapPagination, setApiSystemOverlapPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // useEffect to send mapped archiveData to the API (same as free data page)
  useEffect(() => {
    if (uploadFinished && !archiveCalledRef.current) {
      archiveCalledRef.current = true; // Prevent multiple calls
      console.log('Final archiveData:', archiveData); // Log final archiveData

      // Define the API endpoint using backticks for template literals
      const apiEndpoint = `/archive/add-api`;
      console.log('Sending archiveData to:', apiEndpoint);

      // Map archiveData to the required format with "id" and "status"
      const mappedArchiveData = Object.values(archiveData).map((entry) => ({
        id: entry.id,
        status: entry.status,
      }));

      // Prepare the data to send (ensure it matches the API's expected format)
      const dataToSend = {
        archive: mappedArchiveData, // Adjusted archive structure
        customer_id: currentSendData?.customers?.[0]?.key || null,
        domain_id: currentSendData?.domain?.key || null,
      };
      console.log('Data to send:', dataToSend);

      // Make the POST request using jwtAxios
      jwtAxios
        .post(apiEndpoint, dataToSend)
        .then((response) => {
          console.log('API response:', response);

          // Handle successful response
          dispatch(showMessage('Archive data successfully sent.'));

          // Set sending results to show completion
          setSendingResults({
            success: true,
            message: 'Data sent and archived successfully',
            totalSent: allStatusCount,
            successCount: doneStatusCount,
            errorCount: errorStatusCount,
            errors: errorData,
          });

          // Stop loading and show results
          setSendingLoading(false);

          // Reset archiveData and uploadFinished
          setArchiveData({});
          setUploadFinished(false);
          archiveCalledRef.current = false; // Reset for next time
          // Refresh duplicate data
          fetchDuplicateSentData();
        })
        .catch((error) => {
          console.log('API error:', error);
          // Handle errors
          dispatch(fetchError('Failed to send archive data: ' + error.message));

          // Set error results
          setSendingResults({
            success: false,
            message: 'Failed to archive data: ' + error.message,
            totalSent: allStatusCount,
            successCount: doneStatusCount,
            errorCount: errorStatusCount,
            errors: errorData,
          });

          // Stop loading and show error results
          setSendingLoading(false);
          setUploadFinished(false);
          archiveCalledRef.current = false; // Reset for next time
        });
    }
  }, [uploadFinished, dispatch]);

  // Fetch domains and customers for send functionality
  const fetchDomainsAndCustomers = async () => {
    try {
      // Fetch domains from API
      const domainsResponse = await jwtAxios.get(
        '/duplicate-sent-data/domains',
      );
      console.log('Domains response:', domainsResponse.data);
      if (domainsResponse.data.status === 200) {
        const domainsData = domainsResponse.data.data.map((domain) => ({
          key: domain.id,
          label: domain.domain_name,
          value: domain.id,
          ...domain,
        }));
        console.log('Setting domains:', domainsData);
        setDomains(domainsData);
      }

      // Initially set empty customers array - will be populated when domain is selected
      setCustomers([]);
    } catch (error) {
      console.error('Error fetching domains and customers:', error);
      dispatch(fetchError('Failed to fetch domains and customers'));
    }
  };

  // Fetch customers for modal (email-specific)
  const handleFetchCustomersForModal = async (domainId, duplicateData) => {
    try {
      const currentEmail =
        duplicateData?.duplicate_data?.email || duplicateData?.email;
      if (!domainId || !currentEmail) {
        setCustomers([]);
        return;
      }
      const customersResponse = await jwtAxios.get(
        `/duplicate-sent-data/customers/${domainId}/${encodeURIComponent(
          currentEmail,
        )}`,
      );
      if (customersResponse.data.status === 200) {
        const responseData = customersResponse.data.data;
        const availableCustomers = responseData.customers.map((customer) => ({
          key: customer.id,
          label: customer.user_name,
          value: customer.id,
          ...customer,
        }));
        setCustomers(availableCustomers);
        setEmailSentHistory(responseData.sentTo || []);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error fetching customers by domain for modal:', error);
      setCustomers([]);
      dispatch(fetchError('Failed to fetch customers for selected domain'));
    }
  };

  // Fetch customers list for filter dropdown (domain optional)
  const fetchCustomersListByDomain = useCallback(async (domainId) => {
    try {
      const base = `/customer/search?pageSize=1000&pageNumber=0&term=`;
      const url = domainId
        ? `${base}&domainId=${encodeURIComponent(domainId)}`
        : base;
      const res = await jwtAxios.get(url);
      if (res.data?.status === 200) {
        const availableCustomers = (res.data.data?.data || []).map(
          (customer) => ({
            key: customer.id,
            label: customer.user_name,
            value: customer.id,
            ...customer,
          }),
        );
        setCustomers(availableCustomers);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error fetching customers list for filters:', error);
      setCustomers([]);
    }
  }, []);

  // Enhanced send to customer functionality with SSE
  const handleSendToCustomerEnhanced = async (sendData) => {
    setSendingLoading(true);
    setSendingResults(null);
    setArchiveData({});
    setAllStatusCount(0);
    setDoneStatusCount(0);
    setErrorStatusCount(0);
    setErrorData([]);
    setCurrentSendData(sendData); // Store sendData for use in useEffect
    archiveCalledRef.current = false; // Reset archive call flag

    try {
      const dataId = sendData.originalDataId || sendData.duplicateId;
      const customerIds = sendData.customers.map((c) => c.key);
      // Extract duplicate data (lead details) for error reporting
      const duplicateDataFields = sendData.duplicateData || {};
      console.log(
        'Sending via /data/send-data/:id with dataId and customers:',
        {dataId, customerIds},
      );

      // For each selected customer, stream SSE from /data/send-data/:id
      // Body: { dataIds: [dataId] }
      let completed = 0;
      for (const customerId of customerIds) {
        await new Promise((resolve) => {
          sseRequest(`data/send-data/${customerId}`, {
            contentType: 'application/json',
            body: JSON.stringify({dataIds: [dataId]}),
            onmessage: (message) => {
              console.log('SSE message received:', message);
              console.log('Message event:', message.event);
              console.log('Message data:', message.data);
              console.log('Message keys:', Object.keys(message));

              // Parse the JSON data from the SSE message
              let parsedData;
              try {
                parsedData = JSON.parse(message.data);
                console.log('Parsed SSE data:', parsedData);
              } catch (error) {
                console.error('Failed to parse SSE data:', error);
                return; // continue stream
              }

              // Check the parsed event type
              if (
                message.event?.includes('doneUploadAPI') ||
                parsedData.event?.includes('doneUploadAPI')
              ) {
                setAllStatusCount((prev) => prev + 1);
                setDoneStatusCount((prev) => prev + 1);
              } else if (
                message.event?.includes('errorUploadAPI') ||
                parsedData.event?.includes('errorUploadAPI')
              ) {
                setErrorStatusCount((prev) => prev + 1);
                setAllStatusCount((prev) => prev + 1);
                // Enrich error entry with customer info and full lead details for accurate reporting
                const baseError = remanageDataError(parsedData.data);
                const customerInfo = customers.find(
                  (c) => c.key === customerId,
                );
                const customerName =
                  customerInfo?.user_name ||
                  customerInfo?.label ||
                  customerInfo?.customer_name ||
                  `Customer ${customerId}`;
                const errorMessage =
                  parsedData.error ||
                  baseError?.error ||
                  baseError?.status ||
                  'Failed to send data';
                // Merge duplicate data (lead details) with error info, similar to free-only page
                const enrichedError = {
                  // Include all lead fields from duplicate_data
                  fullName:
                    duplicateDataFields.fullName || baseError?.fullName || '',
                  phone: duplicateDataFields.phone || baseError?.phone || '',
                  age: duplicateDataFields.age || baseError?.age || '',
                  email: duplicateDataFields.email || baseError?.email || '',
                  country:
                    duplicateDataFields.country || baseError?.country || '',
                  marks: duplicateDataFields.marks || baseError?.marks || '',
                  MPC_4: duplicateDataFields.MPC_4 || baseError?.MPC_4 || '',
                  data_source:
                    duplicateDataFields.data_source ||
                    baseError?.data_source ||
                    '',
                  integration_source:
                    duplicateDataFields.integration_source ||
                    baseError?.integration_source ||
                    '',
                  // Include error and customer info
                  ...baseError,
                  id:
                    baseError?.id ||
                    (typeof baseError === 'object'
                      ? baseError?.data_id
                      : undefined) ||
                    dataId,
                  customer_id: customerId,
                  customer: customerName,
                  message: errorMessage,
                  error: errorMessage,
                  timestamp: new Date().toISOString(),
                };
                setErrorData((prev) => [...prev, enrichedError]);
              } else if (
                message.event?.includes('AllDataProcessed') ||
                parsedData.event?.includes('completed')
              ) {
                // Finalize stats
                const successCount = doneStatusCount + 0;
                const errorCount = errorStatusCount + 0;
                const totalCount = successCount + errorCount;
                setSendingResults({
                  success: errorCount === 0,
                  message:
                    errorCount === 0
                      ? `Data sent and archived successfully`
                      : `Completed with ${errorCount} errors`,
                  totalSent: totalCount,
                  successCount,
                  errorCount,
                  errors: errorData,
                });
                setAllStatusCount(totalCount);
                setDoneStatusCount(successCount);
                setErrorStatusCount(errorCount);
                return;
              }

              // Update live progress results
              const isError =
                message.event?.includes('errorUploadAPI') ||
                parsedData.event?.includes('errorUploadAPI');
              const isSuccess =
                message.event?.includes('doneUploadAPI') ||
                parsedData.event?.includes('doneUploadAPI');
              setSendingResults({
                success: null, // Keep as null during progress - only set true/false when complete
                message: `Sending data... (${
                  doneStatusCount + (isSuccess ? 1 : 0)
                } sent, ${errorStatusCount + (isError ? 1 : 0)} errors)`,
                totalSent: allStatusCount + 1,
                successCount: doneStatusCount + (isSuccess ? 1 : 0),
                errorCount: errorStatusCount + (isError ? 1 : 0),
                errors: errorData,
              });

              // Update archiveData
              setArchiveData((prev) => {
                const {id, status} = remanageDataArchive(message.data);
                const newArchiveData = {...prev, [id]: {id, status}};
                return newArchiveData;
              });
            },
            onclose: () => {
              completed += 1;
              if (completed === customerIds.length) {
                setTimeout(() => setUploadFinished(true), 100);
                resolve(undefined);
              }
            },
            onerror: (error) => {
              dispatch(fetchError('Failed: ' + error?.message));
              setSendingLoading(false);
              resolve(undefined);
            },
          });
        });
      }
    } catch (error) {
      console.error('Error initiating SSE send:', error);
      dispatch(fetchError('Failed to initiate sending: ' + error.message));
      setSendingLoading(false);
    }
  };

  // Helper function to remanage archive data (from free data page)
  const remanageDataArchive = (input) => {
    try {
      const parsedInput = typeof input === 'string' ? JSON.parse(input) : input;
      const rawData = parsedInput.data;
      const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      const id = data?.id || 'Unknown ID';
      const status =
        parsedInput.error || (id === 'Unknown ID' ? 'Error' : 'Success');
      return {id, status};
    } catch (error) {
      return {id: 'Unknown ID', status: 'Error'};
    }
  };

  // Helper function to remanage error data (from free data page)
  const remanageDataError = (input) => {
    // Accept either a JSON string or an already-parsed object
    const jsonParsed =
      typeof input === 'string'
        ? (() => {
            try {
              return JSON.parse(input);
            } catch {
              return {data: input};
            }
          })()
        : input || {};
    let data;
    if (typeof jsonParsed?.data === 'string') {
      try {
        data = JSON.parse(jsonParsed.data);
      } catch {
        data = jsonParsed.data;
      }
    } else if (jsonParsed?.data) {
      data = jsonParsed.data;
    } else {
      data = jsonParsed;
    }
    if (data && typeof data === 'object') {
      data.error =
        jsonParsed.error ||
        jsonParsed.originalErrorData ||
        jsonParsed.message ||
        null;
    }
    return data;
  };

  // Fetch duplicate sent data
  const fetchDuplicateSentData = async () => {
    setGettingData(true);
    try {
      const toIsoDay = (d, end = false) => {
        if (!d) return '';
        try {
          const dd =
            d instanceof Date
              ? new Date(d)
              : typeof d === 'string' && d.includes('T')
              ? new Date(d)
              : new Date(`${d}T00:00:00`);
          if (isNaN(dd.getTime())) return '';
          if (end) dd.setHours(23, 59, 59, 999);
          else dd.setHours(0, 0, 0, 0);
          return dd.toISOString();
        } catch (_) {
          return '';
        }
      };

      const params = new URLSearchParams({
        page: (pageNumber + 1).toString(),
        limit: pageSize.toString(),
      });

      // Map filters to query params like DataLG.jsx
      if (filters.country) params.set('country', filters.country);
      if (filters.dataSource) params.set('dataSource', filters.dataSource);
      if (filters.domain) params.set('domainId', String(filters.domain));
      if (filters.customer) params.set('customer', String(filters.customer));
      if (debouncedSearch) params.set('searchParamsData', debouncedSearch);
      if (filters.integrationSource)
        params.set('integrationSource', filters.integrationSource);
      if (Array.isArray(filters.createdDateRange)) {
        const [from, to] = filters.createdDateRange;
        const fromIso = toIsoDay(from, false);
        const toIso = toIsoDay(to, true);
        if (fromIso && toIso) {
          params.set('createdFrom', fromIso);
          params.set('createdTo', toIso);
        }
      }
      if (Array.isArray(filters.sentDateRange)) {
        const [from, to] = filters.sentDateRange;
        const fromIso = toIsoDay(from, false);
        const toIso = toIsoDay(to, true);
        if (fromIso && toIso) {
          params.set('sentFrom', fromIso);
          params.set('sentTo', toIso);
        }
      }

      const response = await jwtAxios.get(`/duplicate-sent-data?${params}`);

      if (response.data.status === 200) {
        const rawData = response.data.data.duplicates || [];

        // Group duplicates by email + phone
        const groupedData = [];
        const duplicateMap = new Map();

        rawData.forEach((item) => {
          const email = item.duplicate_data?.email || item.email || '';
          const phone = item.duplicate_data?.phone || item.phone || '';
          const key = `${email}_${phone}`;

          if (duplicateMap.has(key)) {
            const existing = duplicateMap.get(key);
            existing.count += 1;
            existing.ids.push(item.id);
            existing.items.push(item);
          } else {
            duplicateMap.set(key, {
              ...item,
              count: 1,
              ids: [item.id],
              items: [item],
              isGrouped: true,
            });
          }
        });

        // Convert map to array
        duplicateMap.forEach((groupedItem) => {
          groupedData.push(groupedItem);
        });

        setDataToShow(groupedData);
        setTotalCount(response.data.data.pagination?.total || 0);
        if (
          (response.data.data.pagination?.total || 0) === 0 ||
          groupedData.length === 0
        ) {
          dispatch(showMessage('No data found'));
        }
      } else {
        dispatch(
          fetchError(
            response.data.message || 'Failed to fetch duplicate sent data',
          ),
        );
      }
    } catch (error) {
      console.error('Error fetching duplicate sent data:', error);
      dispatch(fetchError('Failed to fetch duplicate sent data'));
    } finally {
      setGettingData(false);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await jwtAxios.get(
        '/duplicate-sent-data/filter-options',
      );
      if (response.data.status === 200) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Fetch multi-customer duplicates
  const fetchMultiCustomerDuplicates = async () => {
    setMultiCustomerLoading(true);
    try {
      const toIsoDay = (d, end = false) => {
        if (!d) return '';
        try {
          const dd =
            d instanceof Date
              ? new Date(d)
              : typeof d === 'string' && d.includes('T')
              ? new Date(d)
              : new Date(`${d}T00:00:00`);
          if (isNaN(dd.getTime())) return '';
          if (end) dd.setHours(23, 59, 59, 999);
          else dd.setHours(0, 0, 0, 0);
          return dd.toISOString();
        } catch (_) {
          return '';
        }
      };

      const params = new URLSearchParams({
        page: multiCustomerPagination.page.toString(),
        limit: multiCustomerPagination.limit.toString(),
      });

      if (filters.country) params.set('country', filters.country);
      if (filters.dataSource) params.set('dataSource', filters.dataSource);
      if (filters.domain) params.set('domainId', String(filters.domain));
      if (filters.customer) params.set('customer', String(filters.customer));
      if (debouncedSearch) params.set('searchParamsData', debouncedSearch);
      if (filters.integrationSource)
        params.set('integrationSource', filters.integrationSource);
      if (Array.isArray(filters.createdDateRange)) {
        const [from, to] = filters.createdDateRange;
        const fromIso = toIsoDay(from, false);
        const toIso = toIsoDay(to, true);
        if (fromIso && toIso) {
          params.set('createdFrom', fromIso);
          params.set('createdTo', toIso);
        }
      }
      if (Array.isArray(filters.sentDateRange)) {
        const [from, to] = filters.sentDateRange;
        const fromIso = toIsoDay(from, false);
        const toIso = toIsoDay(to, true);
        if (fromIso && toIso) {
          params.set('sentFrom', fromIso);
          params.set('sentTo', toIso);
        }
      }

      const response = await jwtAxios.get(
        `/duplicate-sent-data/multi-customer?${params}`,
      );

      if (response.data.status === 200) {
        const dup = response.data.data.duplicates || [];
        const toTs = (d) => (d ? new Date(d).getTime() : 0);
        const sorted = [...dup].sort((a, b) => {
          const ad = Number(a?.has_recent_duplicate ? 1 : 0);
          const bd = Number(b?.has_recent_duplicate ? 1 : 0);
          if (bd !== ad) return bd - ad; // duplicates first
          const als = toTs(a?.last_sent_at);
          const bls = toTs(b?.last_sent_at);
          if (bls !== als) return bls - als; // last sent desc
          const at = toTs(a?.created_at);
          const bt = toTs(b?.created_at);
          return bt - at; // newest created desc
        });
        setMultiCustomerData(sorted);
        setMultiCustomerPagination((prev) => ({
          ...prev,
          total: response.data.data.pagination?.total || 0,
          totalPages: response.data.data.pagination?.totalPages || 0,
        }));
        if (
          (response.data.data.pagination?.total || 0) === 0 ||
          (response.data.data.duplicates || []).length === 0
        ) {
          dispatch(showMessage('No data found'));
        }
      } else {
        dispatch(
          fetchError(
            response.data.message ||
              'Failed to fetch multi-customer duplicates',
          ),
        );
      }
    } catch (error) {
      console.error('Error fetching multi-customer duplicates:', error);
      dispatch(fetchError('Failed to fetch multi-customer duplicates'));
    } finally {
      setMultiCustomerLoading(false);
    }
  };

  // Fetch API vs System overlap
  const fetchApiSystemOverlap = async () => {
    setApiSystemOverlapLoading(true);
    try {
      const toIsoDay = (d, end = false) => {
        if (!d) return '';
        try {
          const dd =
            d instanceof Date
              ? new Date(d)
              : typeof d === 'string' && d.includes('T')
              ? new Date(d)
              : new Date(`${d}T00:00:00`);
          if (isNaN(dd.getTime())) return '';
          if (end) dd.setHours(23, 59, 59, 999);
          else dd.setHours(0, 0, 0, 0);
          return dd.toISOString();
        } catch (_) {
          return '';
        }
      };

      const params = new URLSearchParams({
        page: apiSystemOverlapPagination.page.toString(),
        limit: apiSystemOverlapPagination.limit.toString(),
      });

      if (filters.country) params.set('country', filters.country);
      if (filters.dataSource) params.set('dataSource', filters.dataSource);
      if (filters.domain) params.set('domainId', String(filters.domain));
      if (filters.customer) params.set('customer', String(filters.customer));
      if (debouncedSearch) params.set('searchParamsData', debouncedSearch);
      if (filters.integrationSource)
        params.set('integrationSource', filters.integrationSource);
      if (Array.isArray(filters.createdDateRange)) {
        const [from, to] = filters.createdDateRange;
        const fromIso = toIsoDay(from, false);
        const toIso = toIsoDay(to, true);
        if (fromIso && toIso) {
          params.set('createdFrom', fromIso);
          params.set('createdTo', toIso);
        }
      }
      if (Array.isArray(filters.sentDateRange)) {
        const [from, to] = filters.sentDateRange;
        const fromIso = toIsoDay(from, false);
        const toIso = toIsoDay(to, true);
        if (fromIso && toIso) {
          params.set('sentFrom', fromIso);
          params.set('sentTo', toIso);
        }
      }

      const response = await jwtAxios.get(
        `/duplicate-sent-data/api-system-overlap?${params}`,
      );

      if (response.data.status === 200) {
        setApiSystemOverlapData(response.data.data.overlaps || []);
        setApiSystemOverlapPagination((prev) => ({
          ...prev,
          total: response.data.data.pagination?.total || 0,
          totalPages: response.data.data.pagination?.totalPages || 0,
        }));
        if (
          (response.data.data.pagination?.total || 0) === 0 ||
          (response.data.data.overlaps || []).length === 0
        ) {
          dispatch(showMessage('No data found'));
        }
      } else {
        dispatch(
          fetchError(
            response.data.message || 'Failed to fetch API vs System overlap',
          ),
        );
      }
    } catch (error) {
      console.error('Error fetching API vs System overlap:', error);
      dispatch(fetchError('Failed to fetch API vs System overlap'));
    } finally {
      setApiSystemOverlapLoading(false);
    }
  };

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 0) {
      fetchMultiCustomerDuplicates();
    } else if (activeTab === 1) {
      fetchDuplicateSentData();
    } else if (activeTab === 2) {
      fetchApiSystemOverlap();
    }
  }, [activeTab, pageNumber, pageSize, effectiveFilters]);

  // Watch for multi-customer pagination changes
  useEffect(() => {
    if (activeTab === 0) {
      fetchMultiCustomerDuplicates();
    }
  }, [
    multiCustomerPagination.page,
    multiCustomerPagination.limit,
    effectiveFilters,
  ]);

  // Load initial data and filter options
  useEffect(() => {
    fetchFilterOptions();
    fetchDomainsAndCustomers();
    fetchMultiCustomerDuplicates();
  }, []);

  // On mount, load all customers for the filter (no domain selected)
  useEffect(() => {
    fetchCustomersListByDomain(null);
  }, [fetchCustomersListByDomain]);

  // Load filter options on component mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // When domain filter changes, refresh customers list for filter dropdown
  useEffect(() => {
    if (typeof filters.domain !== 'undefined') {
      fetchCustomersListByDomain(filters.domain || null);
    }
  }, [filters.domain, fetchCustomersListByDomain]);

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPageNumber(newPage);
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNumber(0);
  };

  // Handle multi-customer page change
  const handleMultiCustomerPageChange = (newPage) => {
    setMultiCustomerLoading(true);
    setMultiCustomerPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  // Handle multi-customer page size change
  const handleMultiCustomerPageSizeChange = (newPageSize) => {
    setMultiCustomerLoading(true);
    setMultiCustomerPagination((prev) => ({
      ...prev,
      page: 1,
      limit: parseInt(newPageSize),
    }));
  };

  // Handle filter change
  const handleFilterChange = (filterName, value) => {
    if (activeTab === 0) {
      setMultiCustomerLoading(true);
    }
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setPageNumber(0);
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  // Handle clear filters
  const handleClearFilters = () => {
    if (activeTab === 0) {
      setMultiCustomerLoading(true);
    }
    setFilters({
      country: '',
      dataSource: '',
      domain: '',
      customer: '',
      search: '',
      createdDateRange: [null, null],
      sentDateRange: [null, null],
      integrationSource: '',
    });
    setPageNumber(0);
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  // Handle edit duplicate
  const handleEditDuplicate = (duplicate) => {
    setEditingDuplicate(duplicate);
    setShowEditModal(true);
  };

  // Handle view customers
  const handleViewCustomers = (customers) => {
    setViewingCustomers(customers);
    setShowCustomerModal(true);
  };

  // Handle opening send modal
  const handleOpenSendModal = (duplicate) => {
    setEditingDuplicate(duplicate);
    setShowSendModal(true);
  };

  // Validate duplicate
  const handleValidateDuplicate = async (duplicateId, editedData) => {
    try {
      const response = await jwtAxios.post(
        `/duplicate-sent-data/${duplicateId}/validate`,
        editedData,
      );

      if (response.data.status === 200) {
        dispatch(showMessage('Validation successful - data is unique'));
        fetchDuplicateSentData();
        return true;
      } else {
        dispatch(fetchError(response.data.message || 'Validation failed'));
        return false;
      }
    } catch (error) {
      dispatch(fetchError('Failed to validate duplicate'));
      return false;
    }
  };

  // Transfer to free data
  const handleTransferToFreeData = async (duplicateId) => {
    try {
      const response = await jwtAxios.post(
        `/duplicate-sent-data/${duplicateId}/transfer`,
      );

      if (response.data.status === 200) {
        dispatch(
          showMessage('Duplicate transferred to free data successfully'),
        );
        fetchDuplicateSentData();
        return {success: true};
      } else {
        const errorMessage = response.data.message || 'Transfer failed';
        dispatch(fetchError(errorMessage));
        return {success: false, error: errorMessage};
      }
    } catch (error) {
      let errorMessage = 'Failed to transfer duplicate';
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      }
      dispatch(fetchError(errorMessage));
      return {success: false, error: errorMessage};
    }
  };

  // Delete duplicate
  const handleDeleteDuplicate = async (duplicateId) => {
    try {
      const response = await jwtAxios.delete(
        `/duplicate-sent-data/${duplicateId}`,
      );

      if (response.data.status === 200) {
        dispatch(showMessage('Duplicate deleted successfully'));
        fetchDuplicateSentData();
      } else {
        dispatch(fetchError(response.data.message || 'Delete failed'));
      }
    } catch (error) {
      dispatch(fetchError('Failed to delete duplicate'));
    }
  };

  // Send to specific customer (non-SSE)
  const handleSendToSpecificCustomer = async (duplicateId, customerId) => {
    try {
      const response = await jwtAxios.post(
        `/duplicate-sent-data/${duplicateId}/send-to-customer/${customerId}`,
      );

      if (response.data.status === 200) {
        dispatch(showMessage('Duplicate sent to customer successfully'));
        fetchDuplicateSentData();
      } else {
        dispatch(fetchError(response.data.message || 'Send failed'));
      }
    } catch (error) {
      dispatch(fetchError('Failed to send duplicate to customer'));
    }
  };

  // Handle update duplicate
  const handleUpdateDuplicate = async (duplicateId, updateData) => {
    try {
      const response = await jwtAxios.put(
        `/duplicate-sent-data/${duplicateId}`,
        updateData,
      );

      if (response.data.status === 200) {
        dispatch(showMessage('Duplicate updated successfully'));
        fetchDuplicateSentData();
        return true;
      } else {
        dispatch(fetchError(response.data.message || 'Update failed'));
        return false;
      }
    } catch (error) {
      dispatch(fetchError('Failed to update duplicate'));
      return false;
    }
  };

  // Close modals
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingDuplicate(null);
  };

  const handleCloseCustomerModal = () => {
    setShowCustomerModal(false);
    setViewingCustomers([]);
  };

  const handleViewLead = (lead) => {
    setViewingLead(lead);
    setShowLeadModal(true);
  };

  const handleCloseLeadModal = () => {
    setShowLeadModal(false);
    setViewingLead(null);
  };

  const handleCloseSendModal = () => {
    setShowSendModal(false);
    setEditingDuplicate(null);
    if (activeTab === 0) {
      fetchMultiCustomerDuplicates();
    } else if (activeTab === 1) {
      fetchDuplicateSentData();
    } else if (activeTab === 2) {
      fetchApiSystemOverlap();
    }
  };

  if (!user) {
    return <AppInfoView />;
  }

  return (
    <DuplicateSentDataUI
      // Data
      dataToShow={dataToShow}
      totalCount={totalCount}
      gettingData={gettingData}
      // Pagination
      pageNumber={pageNumber}
      pageSize={pageSize}
      handlePageChange={handlePageChange}
      handlePageSizeChange={handlePageSizeChange}
      // Filters
      filters={filters}
      filterOptions={filterOptions}
      handleFilterChange={handleFilterChange}
      handleClearFilters={handleClearFilters}
      // Selection
      selected={selected}
      setSelected={setSelected}
      // Actions
      onEditDuplicate={handleEditDuplicate}
      onViewCustomers={handleViewCustomers}
      onViewLead={handleViewLead}
      onSendToCustomer={handleOpenSendModal}
      onSendToCustomerEnhanced={handleSendToCustomerEnhanced}
      onFetchCustomersListByDomain={fetchCustomersListByDomain}
      onFetchCustomersForModal={handleFetchCustomersForModal}
      sendingLoading={sendingLoading}
      sendingResults={sendingResults}
      domains={domains}
      customers={customers}
      emailSentHistory={emailSentHistory}
      onValidateDuplicate={handleValidateDuplicate}
      onTransferToFreeData={handleTransferToFreeData}
      onDeleteDuplicate={handleDeleteDuplicate}
      onRefresh={fetchDuplicateSentData}
      onRefreshMulti={fetchMultiCustomerDuplicates}
      onSendToSpecificCustomer={handleSendToSpecificCustomer}
      onUpdateDuplicate={handleUpdateDuplicate}
      // Modals
      showEditModal={showEditModal}
      showCustomerModal={showCustomerModal}
      showSendModal={showSendModal}
      editingDuplicate={editingDuplicate}
      viewingCustomers={viewingCustomers}
      onCloseEditModal={handleCloseEditModal}
      onCloseCustomerModal={handleCloseCustomerModal}
      onCloseLeadModal={handleCloseLeadModal}
      onCloseSendModal={handleCloseSendModal}
      // SSE Progress tracking props
      allStatusCount={allStatusCount}
      doneStatusCount={doneStatusCount}
      errorStatusCount={errorStatusCount}
      errorData={errorData}
      // New tab data
      multiCustomerData={multiCustomerData}
      multiCustomerLoading={multiCustomerLoading}
      multiCustomerPagination={multiCustomerPagination}
      apiSystemOverlapData={apiSystemOverlapData}
      apiSystemOverlapLoading={apiSystemOverlapLoading}
      apiSystemOverlapPagination={apiSystemOverlapPagination}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      onMultiCustomerSearch={(searchTerm) => {
        setFilters((prev) => ({...prev, search: searchTerm}));
      }}
      onApiSystemOverlapSearch={(searchTerm) => {
        setFilters((prev) => ({...prev, search: searchTerm}));
      }}
      onMultiCustomerPageChange={handleMultiCustomerPageChange}
      onMultiCustomerPageSizeChange={handleMultiCustomerPageSizeChange}
      // Lead modal
      showLeadModal={showLeadModal}
      viewingLead={viewingLead}
    />
  );
};

export default DuplicateSentDataLG;
